import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { DsDropdownItemModel } from "@sazka/design-system/src/forms/components/ds-dropdown/ds-dropdown-models";
import DrawSelectorAttributes, {
    BetDrawPeriodValue,
} from "@sazka/lotteries/js/models/bet-online/draw-selector-attributes";
import { BetDates } from "@sazka/lotteries/js/models/draw-info/future-draw-dto";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { BetDetail } from "@sazka/lotteries/js/models/my-tickets/bet-detail";
import { SubscriptionDetail } from "@sazka/lotteries/js/models/subscriptions/subscription-detail";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { QuickWagerCard } from "@/lotteries/category-page/model/quick-wager-card";
import { fetchBetDates } from "@/lotteries/services/draw-info/api";
import { usePpWagerConfiguration } from "../composables/use-wager-configuration";
import { BettingMode } from "../model/betting-mode";
import { DurationState } from "../model/duration-state";
import { SubscriptionEndType } from "../model/subscription-end-type";

export const usePpWagerDurationStore = () => {
    const config = usePpWagerConfiguration().config;

    const { lottery } = config;

    if (!lottery) {
        console.error("Wager configuration is not set. Please configure using usePpWagerConfiguration.");
        return;
    }

    const store = defineStore(
        "wager-duration-store-" + lottery,
        () => {
            // State
            const durationType = ref<DurationState>(DurationState.Once);
            const duration = ref(1);
            const drawNames = ref<string[]>([]);
            const notification = ref(false);
            const subscriptionEndType = ref<SubscriptionEndType | null>(SubscriptionEndType.Unlimited);
            const subscriptionEnd = ref<Date | null>(null);
            const subscriptionName = ref<string | null>(null);
            const isDurationLimited = ref(false);
            const durationLimit = ref(0);
            const firstBetDate = ref<Date | null>(null);
            const drawDates = ref<BetDates | null>(null);
            const allDraws = ref(config.multidrawDurationOptions?.map((o) => o.value));
            const minDuration = ref(config.hasDurationInDays ? 1 : 2);
            const ticketCheckDuration = ref(1);

            // Getters
            const getMaxDuration = computed(() => {
                const maxDuration = isDurationLimited.value ? durationLimit.value : config.maxDuration;

                // keep max duration for lotteries with no or one draw option
                if (!config.multidrawDurationOptions?.length || config.multidrawDurationOptions.length === 1) {
                    return maxDuration;
                }

                if (config.hasDurationInDays) {
                    return Math.floor(maxDuration / config.multidrawDurationOptions.length);
                }

                // keep max duration if all draw options are selected
                const drawsCount = drawNames.value?.length ? drawNames.value.length : 1;
                if (drawsCount > 1) {
                    return maxDuration;
                }

                // if single option is selected split duration by options
                let drawsForOneOption = Math.floor(maxDuration / config.multidrawDurationOptions.length);
                if (!isDurationLimited.value) {
                    return drawsForOneOption;
                }

                // in case of duration shortening options can have different max duration, increase max duration by modulo
                const remainingDraws = maxDuration % config.multidrawDurationOptions.length;
                const orderedDrawOptions = drawDates.value?.draws?.map((d) => LotteryParser.getDrawNameValue(d));
                if (orderedDrawOptions.indexOf(drawNames.value[0] as BetDrawPeriodValue) < remainingDraws) {
                    drawsForOneOption++;
                }

                return drawsForOneOption;
            });

            const generateDurationOption = (duration: number): DsDropdownItemModel => {
                const label = (
                    config.hasDurationInDays
                        ? `${duration} ${LotteryParser.getDayInflectionLabel(duration)}`
                        : `${duration} slosování`
                ).toUpperCase();

                return { value: duration, label };
            };

            const getValidDurationOptions = computed((): DsDropdownItemModel[] => {
                if (config.durationOptions?.length)
                    return config.durationOptions.map((duration) => generateDurationOption(duration));

                const maxOptions = config.hasDurationInDays ? getMaxDuration.value : getMaxDuration.value - 1;
                return [...Array(maxOptions).keys()].map((i) => generateDurationOption(i + minDuration.value));
            });

            const getDurationLengthFromDays = computed(() => {
                return duration.value * (drawNames.value?.length || 1);
            });

            /**
             * Returns the actual duration length (even for lotteries which have duration in days)
             */
            const getDurationLength = computed(() => {
                // returning anything else than 1 breaks API
                if (durationType.value === DurationState.Subscription) return 1;

                return config.hasDurationInDays ? getDurationLengthFromDays.value : duration.value;
            });

            const getBettingMode = computed((): BettingMode => {
                switch (durationType.value) {
                    case DurationState.Once:
                        return BettingMode.singleDraw;
                    case DurationState.Multi:
                        return BettingMode.multiDraw;
                    case DurationState.Subscription:
                        return BettingMode.standingOrder;
                    default:
                        return BettingMode.singleDraw;
                }
            });

            const getRepetitionEndDate = computed((): string | null => {
                if (durationType.value === DurationState.Subscription) {
                    if (subscriptionEndType.value === SubscriptionEndType.Unlimited) {
                        return "infinity";
                    } else {
                        return DateTimeUtils.formatDate(subscriptionEnd.value).replace(/\s/g, "");
                    }
                }
                return null;
            });

            const getSubscriptionMinEndDate = computed(() => {
                if (!drawNames.value?.length) {
                    return drawDates.value?.draws?.[0];
                }

                return drawDates.value?.draws?.find((d) =>
                    drawNames.value?.includes(LotteryParser.getDrawNameValue(d))
                );
            });

            const getSubscriptionMaxEndDate = computed(() => {
                if (isDurationLimited.value) {
                    const changeDateString = (window as any).rulesChangeDate;
                    return new Date(changeDateString);
                }

                return new Date(2099, 11, 31);
            });

            const getDefaultDrawNames = (isNewInit = true, includeSpecialDraw = false) => {
                if (durationType.value === DurationState.Once) {
                    if (!drawDates.value?.draws?.length) {
                        return [];
                    }

                    if (
                        drawDates.value?.draws?.length == 1 ||
                        (drawDates.value?.draws?.length > 1 && drawNames.value?.length != 1) ||
                        isNewInit
                    ) {
                        if (
                            includeSpecialDraw &&
                            drawDates.value?.specialDraw &&
                            new Date(drawDates.value?.specialDraw?.drawDate) <
                                new Date(drawDates.value?.draws?.[0]?.drawDate)
                        ) {
                            return [LotteryParser.getDrawNameValue(drawDates.value.specialDraw, true)];
                        }

                        return [LotteryParser.getDrawNameValue(drawDates.value?.draws?.[0])];
                    }
                } else if (!drawNames.value?.length || isNewInit) {
                    return config.hasAllOrOneMultidrawOptions
                        ? allDraws.value
                        : config.multidrawDurationOptions.map((x) => x.value);
                }

                return drawNames.value;
            };

            // Actions
            const handleLimitedDurationOverflow = () => {
                if (durationLimit.value === 1) {
                    durationType.value = DurationState.Once;
                    duration.value = 1;
                    drawNames.value = [LotteryParser.getDrawNameValue(drawDates.value?.draws?.[0])];
                    return;
                }

                if (duration.value > getMaxDuration.value) {
                    durationType.value = DurationState.Once;
                    duration.value = 1;
                    if (drawNames.value?.length != 1) {
                        drawNames.value = [LotteryParser.getDrawNameValue(drawDates.value?.draws?.[0])];
                    }
                }
            };

            const initDuration = (
                initialDurationType: DurationState = DurationState.Once,
                initialDuration = 1,
                initialDrawNames: string[] = [],
                initialNotification = false,
                initialSubscriptionEnd: Date | null = null,
                initialSubscriptionName: string | null = null,
                includeSpecialDraw = false
            ) => {
                durationType.value =
                    initialDurationType || (initialDuration > 1 ? DurationState.Multi : DurationState.Once);
                duration.value = initialDuration || 1;
                notification.value = initialNotification;
                subscriptionName.value = initialSubscriptionName;

                if (initialDrawNames?.length) {
                    drawNames.value = initialDrawNames;
                } else {
                    drawNames.value = getDefaultDrawNames(true, includeSpecialDraw);
                }

                if (durationType.value === DurationState.Multi && config.hasDurationInDays) {
                    duration.value = Math.floor(duration.value / drawNames.value.length) || 1;
                }

                if (durationType.value === DurationState.Subscription) {
                    subscriptionEnd.value =
                        typeof initialSubscriptionEnd === "string"
                            ? new Date(initialSubscriptionEnd)
                            : initialSubscriptionEnd;
                } else {
                    subscriptionEnd.value = null;
                }

                subscriptionEndType.value = subscriptionEnd.value
                    ? SubscriptionEndType.Date
                    : SubscriptionEndType.Unlimited;

                if (isDurationLimited.value) {
                    handleLimitedDurationOverflow();
                }
            };

            const isDurationValid = computed(() => {
                if (!durationType.value) {
                    return false;
                }

                if (getDurationLength.value === 0) {
                    return false;
                }

                if (
                    durationType.value === DurationState.Subscription &&
                    subscriptionEndType.value === SubscriptionEndType.Date &&
                    !subscriptionEnd.value
                ) {
                    return false;
                }

                if (!config.hasDrawOptions) {
                    return true;
                }

                if (!drawNames.value?.length) {
                    return false;
                }

                if (
                    durationType.value === DurationState.Multi &&
                    drawNames.value?.length === 1 &&
                    drawNames.value.includes("special")
                ) {
                    return false;
                }

                return true;
            });

            const isClosestDrawSelected = computed(() => {
                if (!drawDates.value?.draws[0]) return false;

                if (
                    drawDates.value.specialDraw &&
                    new Date(drawDates.value.specialDraw.drawDate) < new Date(drawDates.value.draws[0].drawDate)
                ) {
                    return drawNames.value.includes(LotteryParser.getDrawNameValue(drawDates.value.specialDraw, true));
                }

                return drawNames.value.includes(LotteryParser.getDrawNameValue(drawDates.value.draws[0]));
            });

            function setFromQuickWager(quickWager: QuickWagerCard) {
                const duration = quickWager.wager.duration ?? 1;
                const initialDrawNames =
                    lottery === Lottery.stastnych10 && duration > 1
                        ? config.multidrawDurationOptions.map((x) => x.value)
                        : [];
                initDuration(duration > 1 ? DurationState.Multi : DurationState.Once, duration, initialDrawNames);
            }

            const getDrawNameBasedOnTime = (drawTime: string, drawSelectorAttributes: DrawSelectorAttributes[]) => {
                const date = new Date(drawTime);
                if (drawSelectorAttributes[0].dayOfWeek !== undefined) {
                    const actualDay = date.getDay();
                    return drawSelectorAttributes.find((o) => Number(o.dayOfWeek) === actualDay)?.value;
                } else if (drawSelectorAttributes[0].hour !== undefined) {
                    const actualHour = date.getHours();
                    return drawSelectorAttributes.find((o) => Number(o.hour) === actualHour)?.value;
                }
                return null;
            };

            const setFromBetDetail = (betDetail: BetDetail) => {
                let drawNames: string[];
                if (config.hasDrawOptions) {
                    drawNames = betDetail.drawNames?.map((d) => d.toLowerCase());

                    if (config.multidrawDurationOptions?.length && drawNames?.length !== 1 && betDetail.duration == 1) {
                        const drawName = getDrawNameBasedOnTime(
                            betDetail.draws[0].drawTime,
                            config.multidrawDurationOptions
                        );
                        drawNames = drawName ? [drawName] : drawNames?.slice(0, 1);
                    }
                }

                initDuration(
                    betDetail.duration > 1 ? DurationState.Multi : DurationState.Once,
                    betDetail.duration,
                    drawNames
                );
            };

            const setFromSubscriptionDetail = (betDetail: SubscriptionDetail) => {
                const drawNames = betDetail.drawNames?.map((d) => d.toLowerCase());
                initDuration(DurationState.Subscription, null, drawNames, false, null, betDetail.name);
            };

            const setDurationLengthToDays = () => {
                if (durationType.value === DurationState.Multi) {
                    duration.value = duration.value / drawNames.value.length;
                }
            };

            // Initialization
            const clearDuration = () => {
                initDuration();
            };

            const fetchDrawDates = async () => {
                drawDates.value = await fetchBetDates(config.lottery);

                if (drawDates.value?.draws.length > 0) {
                    firstBetDate.value = new Date(drawDates.value?.draws[0].drawDate);
                }

                drawNames.value = getDefaultDrawNames(false);
            };

            // Change Handlers
            const handleDurationTypeChange = () => {
                if (durationType.value === DurationState.Multi || durationType.value === DurationState.Subscription) {
                    if (config.hasAllOrOneMultidrawOptions) {
                        drawNames.value = allDraws.value;
                    } else {
                        drawNames.value = config.multidrawDurationOptions.map((x) => x.value);
                    }
                }

                if (durationType.value === DurationState.Multi && duration.value < minDuration.value) {
                    duration.value = minDuration.value;
                }

                if (durationType.value !== DurationState.Multi) {
                    duration.value = 1;
                }

                drawNames.value = getDefaultDrawNames(false);
            };

            const handleSubscriptionEnd = () => {
                if (
                    subscriptionEnd.value &&
                    subscriptionEnd.value < new Date(getSubscriptionMinEndDate.value?.drawDate)
                ) {
                    subscriptionEnd.value = new Date(getSubscriptionMinEndDate.value?.drawDate);
                }
            };

            watch(
                drawNames,
                () => {
                    handleSubscriptionEnd();

                    if (duration.value > getMaxDuration.value) duration.value = getMaxDuration.value;
                },
                { deep: true }
            );

            return {
                // State
                allDraws,
                durationType,
                duration,
                drawNames,
                notification,
                subscriptionEndType,
                subscriptionEnd,
                subscriptionName,
                isDurationLimited,
                durationLimit,
                firstBetDate,
                drawDates,
                ticketCheckDuration,
                isClosestDrawSelected,

                // Getters
                getMaxDuration,
                getValidDurationOptions,
                getDurationLength,
                getBettingMode,
                getRepetitionEndDate,
                getDurationLengthFromDays,
                getSubscriptionMinEndDate,
                getSubscriptionMaxEndDate,
                isDurationValid,

                // Actions
                initDuration,
                handleLimitedDurationOverflow,
                handleDurationTypeChange,
                setFromSubscriptionDetail,
                setFromBetDetail,
                setDurationLengthToDays,
                fetchDrawDates,
                setFromQuickWager,
                clearDuration,
                generateDurationOption,
            };
        },
        {
            persist: {
                // eslint-disable-next-line no-restricted-globals
                storage: sessionStorage,
                pick: [
                    "durationType",
                    "duration",
                    "drawNames",
                    "notification",
                    "subscriptionEndType",
                    "subscriptionEnd",
                ],
            },
        }
    );

    return store();
};
